import { z } from "zod";

/** Super-Admin-Bereich und Support-Zugang. */

export type SupportStatus = "requested" | "granted" | "denied" | "revoked";

export const SUPPORT_STATUS_LABELS: Record<SupportStatus, string> = {
  requested: "Angefragt",
  granted: "Erlaubt",
  denied: "Abgelehnt",
  revoked: "Beendet",
};

/** Wie lange eine Firma den Support in ihr Konto lassen kann. */
export const SUPPORT_HOURS = [1, 4, 24, 72] as const;

export interface SupportRequestInfo {
  id: string;
  reason: string;
  status: SupportStatus;
  createdAt: string;
  expiresAt: string | null;
  usedAt: string | null;
  /** Erlaubt und noch nicht abgelaufen. */
  active: boolean;
}

export interface AdminStats {
  companies: { total: number; active: number; pending: number; blocked: number };
  founders: { assigned: number; total: number };
  users: number;
  projects: number;
  files: number;
  storageBytes: number;
  activeCompanies7: number;
  activeCompanies30: number;
  /** Letzte 30 Tage, ältester Tag zuerst. */
  days: { day: string; registered: number; blocked: number; verified: number }[];
  blockReasons: { reason: string; count: number }[];
  usage: { kind: string; count: number }[];
}

export interface AdminCompany {
  id: string;
  name: string;
  trade: string | null;
  status: "pending" | "active" | "blocked";
  planLabel: string;
  /** Zusatzabo 1 (Zeichenprogramm) freigeschaltet. */
  addonPlans: boolean;
  /** Angebote, Aufträge und Rechnungen freigeschaltet. */
  addonBilling: boolean;
  founderNumber: number | null;
  ownerName: string | null;
  ownerEmail: string | null;
  users: number;
  projects: number;
  files: number;
  storageBytes: number;
  createdAt: string;
  lastActivity: string | null;
  support: SupportRequestInfo | null;
}

export interface AdminMailSettings {
  /** Woher die aktuell genutzten Einstellungen stammen. */
  source: "admin" | "umgebung" | "keine";
  /** smtp: eigener Mailserver mit Passwort; microsoft: Versand über Microsoft 365 mit der App-Registrierung, ohne Passwort. */
  mode: "smtp" | "microsoft";
  /** Ob die App-Registrierung bei Microsoft (Tab „Outlook“) eingetragen ist. */
  microsoftReady: boolean;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  hasPassword: boolean;
  from: string;
}

export const mailSettingsSchema = z.object({
  host: z.string().trim().min(3, "Bitte den Mailserver angeben, z. B. smtp.office365.com.").max(200),
  port: z.number().int().min(1, "Der Port ist ungültig.").max(65535, "Der Port ist ungültig."),
  secure: z.boolean(),
  user: z.string().trim().max(254),
  /** Leer lassen, um das gespeicherte Passwort zu behalten. */
  pass: z.string().max(500).optional(),
  from: z.string().trim().min(3, "Bitte einen Absender angeben, z. B. HandVero <service@handvero.de>.").max(254),
});
export type MailSettingsInput = z.infer<typeof mailSettingsSchema>;

/** Versand über Microsoft 365: nur die Absenderadresse, Anmeldung über die App-Registrierung. */
export const microsoftMailSchema = z.object({
  from: z
    .string()
    .trim()
    .max(254)
    .refine((v) => /^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/.test(v.match(/<([^>]+)>/)?.[1] ?? v), "Bitte eine Absenderadresse angeben, z. B. HandVero <service@handvero.de>."),
});

export const supportRequestSchema = z.object({
  reason: z.string().trim().min(5, "Bitte kurz schreiben, wobei der Support helfen soll.").max(500, "Der Grund ist zu lang."),
});

export const supportDecisionSchema = z.object({
  allow: z.boolean(),
  hours: z
    .number()
    .refine((h) => (SUPPORT_HOURS as readonly number[]).includes(h), "Bitte eine der angebotenen Zeiten wählen.")
    .optional(),
});

export const companyStatusSchema = z.object({ status: z.enum(["active", "blocked"]) });

export interface AdminMicrosoftSettings {
  source: "admin" | "umgebung" | "keine";
  clientId: string;
  hasSecret: boolean;
  /** Diese Adresse muss bei Microsoft als Umleitungs-URI eingetragen sein. */
  redirectUri: string;
  connections: number;
}

export const microsoftSettingsSchema = z.object({
  clientId: z
    .string()
    .trim()
    .regex(/^[0-9a-f-]{36}$/i, "Die Anwendungs-ID sieht aus wie 1a2b3c4d-1234-…, bitte aus dem Azure-Portal kopieren."),
  clientSecret: z.string().trim().max(500).optional(),
});
